import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Building, Search, Mail, LogOut, Users, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrandDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('discovery');
  const [influencers, setInfluencers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'discovery') {
      fetchInfluencers();
    }
  }, [activeTab, searchQuery]);

  const fetchInfluencers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/brand/influencers`, {
        params: { search: searchQuery || undefined }
      });
      setInfluencers(response.data);
    } catch (error) {
      console.error('Error fetching influencers:', error);
      toast.error('Failed to load influencers');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestConnection = (influencer) => {
    toast.info(`Connection request for ${influencer.name} will be sent to admin for approval`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <h2>Brand Dashboard</h2>
          <p>{user?.name}</p>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'discovery' ? 'active' : ''}`}
            onClick={() => setActiveTab('discovery')}
            data-testid="discovery-tab"
          >
            <Search size={20} />
            <span>Discover Influencers</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
            data-testid="messages-tab"
          >
            <Mail size={20} />
            <span>Messages</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <Button onClick={() => navigate('/')} variant="ghost" className="nav-btn">
            Back to Home
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="nav-btn logout-btn">
            <LogOut size={18} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {activeTab === 'discovery' && (
          <div className="dashboard-content">
            <h1 className="content-title">Discover Influencers</h1>

            {/* Search Bar */}
            <div className="search-section">
              <div className="search-container-dash">
                <Search className="search-icon-dash" />
                <Input
                  type="text"
                  placeholder="Search by name, category, or niche..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input-dash"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Influencers Grid */}
            {loading ? (
              <div className="loading-state">
                <div className="loader"></div>
                <p>Loading influencers...</p>
              </div>
            ) : influencers.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>No influencers found</p>
              </div>
            ) : (
              <div className="influencers-grid-dash">
                {influencers.map((influencer) => (
                  <div key={influencer.id} className="influencer-card-dash" data-testid={`influencer-${influencer.id}`}>
                    <div className="card-image-dash">
                      {influencer.profile_image_url ? (
                        <img src={influencer.profile_image_url} alt={influencer.name} />
                      ) : (
                        <div className="placeholder-image-dash">
                          <Users size={48} />
                        </div>
                      )}
                    </div>
                    
                    <div className="card-content-dash">
                      <h3 className="card-name-dash">{influencer.name}</h3>
                      <p className="card-bio-dash">{influencer.bio.substring(0, 80)}...</p>
                      
                      <div className="card-categories-dash">
                        {influencer.categories.slice(0, 3).map((cat, idx) => (
                          <span key={idx} className="category-tag-dash">{cat}</span>
                        ))}
                      </div>
                      
                      <Button
                        onClick={() => handleRequestConnection(influencer)}
                        className="connect-btn-dash"
                        data-testid={`connect-${influencer.id}`}
                      >
                        <MessageSquare size={16} />
                        Request Connection
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="dashboard-content">
            <h1 className="content-title">Messages</h1>
            <div className="empty-state">
              <Mail size={48} />
              <p>No messages yet</p>
              <p className="empty-subtitle">Messages from connected influencers will appear here</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BrandDashboard;